import {useState, useRef, useCallback, useMemo, useEffect, useId} from 'react'
import {clsx} from 'clsx'
import {Banner, IconButton, PortalContext, registerPortalRoot, Spinner, TreeView, useOnEscapePress} from '@primer/react'
import {SidebarCollapseIcon, SidebarExpandIcon, XIcon} from '@primer/octicons-react'
import {DiffFileHeader} from '@github-ui/diff-file-header'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {StaticDiffLine} from '@github-ui/diff-lines/conversation-types'
import {useDiffSource} from '../../hooks/use-diff-source'
import {announce} from '@github-ui/aria-live'
import {useTaskDiffs} from '../../hooks/use-task-diffs'
import {parseUnifiedDiff} from '../../utils/parse-unified-diff'
import {UNPREVIEWABLE_REASON} from '../../utils/resolve-changeset-file-content'
import {DiffFileTree} from '@github-ui/diff-file-tree/file-tree'
import type {FileProps} from '@github-ui/diff-file-tree/file-tree'
import type {DiffDelta} from '@github-ui/diff-file-tree/diff-file-tree-helpers'
import {FileStatusIcon} from '@github-ui/diff-file-tree/file-status-icon'
import type {DiffData} from '../../types/diff'
import {blobPath} from '@github-ui/paths'
import styles from './DiffOverlay.module.css'

const MOBILE_BREAKPOINT = 768

// Map our short status codes to PatchStatus strings used by the file tree
function mapStatus(status: string): string {
  switch (status) {
    case 'A':
      return 'ADDED'
    case 'D':
      return 'DELETED'
    case 'R':
      return 'RENAMED'
    case 'C':
      return 'COPIED'
    case 'M':
    default:
      return 'MODIFIED'
  }
}

function toDiffDeltas(files: DiffData['files']): DiffDelta[] {
  return files.map(file => ({
    path: file.path,
    pathDigest: file.path_digest,
    changeType: mapStatus(file.status),
  }))
}

interface DiffOverlayProps {
  isOpen: boolean
  onClose: () => void
  repoNwo: string
  taskId: string
  baseRef: string
  headRef: string
  initialFilePath?: string | null
  withinDialog?: boolean
}

export function DiffOverlay({
  isOpen,
  onClose,
  repoNwo,
  taskId,
  baseRef,
  headRef,
  initialFilePath,
  withinDialog = false,
}: DiffOverlayProps) {
  const {
    changeset,
    hasChangesetDiffs,
    changesetFilesReady,
    diffs: changesetDiffs,
    isLoading: changesetContentLoading,
    contentUnavailable,
  } = useDiffSource(isOpen)
  // REST always runs in parallel with the changeset for a branch-backed session (never gated on
  // changeset pendingness) so a slow-computing changeset can't stall an otherwise-ready REST diff.
  const restViable = !!baseRef && !!headRef
  const changesetPending = changeset.available && !changesetFilesReady && !changeset.error
  const {data: legacyDiffs, isLoading: legacyLoading} = useTaskDiffs(repoNwo, taskId, baseRef, headRef, {
    includePatch: true,
    enabled: isOpen && !hasChangesetDiffs && restViable,
  })
  const diffs = hasChangesetDiffs ? changesetDiffs : legacyDiffs
  // changesetPending only matters as a loading fallback for changeset-only sessions (no branch to
  // fall back to REST for) — branch-backed sessions reflect legacyLoading directly instead.
  const isLoading = hasChangesetDiffs ? changesetContentLoading : restViable ? legacyLoading : changesetPending

  const overlayRef = useRef<HTMLDivElement>(null)
  const fileTreePortalId = useId()
  const fileTreePortalContext = useMemo(() => ({portalContainerName: fileTreePortalId}), [fileTreePortalId])
  const setOverlayRef = useCallback(
    (element: HTMLDivElement | null) => {
      overlayRef.current = element
      registerPortalRoot(element ?? document.body, fileTreePortalId)
    },
    [fileTreePortalId],
  )
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useOnEscapePress(() => {
    if (isOpen && !withinDialog) onClose()
  }, [onClose, isOpen, withinDialog])
  useEffect(() => {
    if (isOpen && changeset.error) announce(changeset.error)
  }, [isOpen, changeset.error])
  // Per-file read failures settle silently (the hook's overall status is still 'ready') — the
  // `changeset.error` announcement above doesn't cover them, so screen reader users otherwise get
  // no signal that some diffs didn't load. Announce once per distinct failed-file set so toggling
  // an unrelated row or reconnect retries that succeed don't repeat it.
  const unavailableFilePaths = useMemo(
    () =>
      hasChangesetDiffs && !changesetContentLoading && !contentUnavailable && changesetDiffs
        ? changesetDiffs.files
            .filter(file => file.previewUnavailableReason === UNPREVIEWABLE_REASON.UNAVAILABLE)
            .map(file => file.path)
        : [],
    [hasChangesetDiffs, changesetContentLoading, contentUnavailable, changesetDiffs],
  )
  const announcedUnavailableRef = useRef('')
  useEffect(() => {
    if (!isOpen) {
      announcedUnavailableRef.current = ''
      return
    }
    const signature = unavailableFilePaths.join('\n')
    if (!signature || announcedUnavailableRef.current === signature) return
    announcedUnavailableRef.current = signature
    announce(
      unavailableFilePaths.length === 1
        ? `Could not load the diff for ${unavailableFilePaths[0]}.`
        : `Could not load the diff for ${unavailableFilePaths.length} files.`,
    )
  }, [isOpen, unavailableFilePaths])
  const [isMobile, setIsMobile] = useState(false)
  const [fileTreeOverride, setFileTreeOverride] = useState<boolean | null>(null)

  useEffect(() => {
    if (!isOpen || withinDialog) return

    const overlay = overlayRef.current
    const returnFocusTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const siblings = overlay
      ? [...(overlay.parentElement?.children ?? [])].filter(
          (element): element is HTMLElement => element instanceof HTMLElement && element !== overlay,
        )
      : []
    const previousInertStates = siblings.map(element => [element, element.inert] as const)

    for (const sibling of siblings) sibling.inert = true
    closeButtonRef.current?.focus()

    return () => {
      for (const [sibling, wasInert] of previousInertStates) sibling.inert = wasInert
      if (returnFocusTarget?.isConnected) requestAnimationFrame(() => returnFocusTarget.focus())
    }
  }, [isOpen, withinDialog])

  // Sync isMobile with container width via ResizeObserver.
  // Also resets fileTreeOverride when crossing the breakpoint so the file tree
  // collapses/expands to its default state for the new size.
  useEffect(() => {
    const el = overlayRef.current
    if (!el) return
    let prevWidth: number | null = null
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      const width = entry.contentRect.width
      const nowMobile = width <= MOBILE_BREAKPOINT
      const wasMobile = prevWidth !== null && prevWidth <= MOBILE_BREAKPOINT
      if (prevWidth !== null && nowMobile !== wasMobile) {
        setFileTreeOverride(null)
      }
      prevWidth = width
      setIsMobile(nowMobile)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [isOpen])

  const showFileTree = fileTreeOverride ?? !isMobile

  const diffPanelRef = useRef<HTMLDivElement>(null)

  const scrollToFile = useCallback(
    (filePath: string) => {
      const panel = diffPanelRef.current
      if (!panel) return
      const element = panel.querySelector<HTMLElement>(`[data-file-path="${CSS.escape(filePath)}"]`)
      if (element) {
        panel.scrollTo({top: element.offsetTop - panel.offsetTop, behavior: 'smooth'})
      }
    },
    [diffPanelRef],
  )

  const handleFileSelect = useCallback(
    (filePath: string) => {
      scrollToFile(filePath)
      if (isMobile) {
        setFileTreeOverride(false)
      }
    },
    [scrollToFile, isMobile],
  )

  const diffDeltas = diffs ? toDiffDeltas(diffs.files) : []

  const fileNodeRenderer = useCallback(
    ({file}: FileProps) => {
      return (
        <TreeView.Item id={file.diff.path} onSelect={() => handleFileSelect(file.filePath)} key={file.diff.pathDigest}>
          <TreeView.LeadingVisual>
            <FileStatusIcon status={file.diff.changeType} />
          </TreeView.LeadingVisual>
          {file.fileName}
        </TreeView.Item>
      )
    },
    [handleFileSelect],
  )

  // Scroll to initial file once diffs are loaded
  useEffect(() => {
    if (isOpen && initialFilePath && diffs && !isLoading) {
      scrollToFile(initialFilePath)
    }
  }, [isOpen, initialFilePath, diffs, isLoading, scrollToFile])

  if (!isOpen) {
    return null
  }

  return (
    <div className={styles.overlay} ref={setOverlayRef} role="region" aria-label="Changes">
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerTitle}>
            {diffs ? `${diffs.files.length} file${diffs.files.length !== 1 ? 's' : ''} changed` : 'Changed files'}
          </span>
        </div>
        <div className={styles.headerActions}>
          <IconButton
            icon={showFileTree ? SidebarCollapseIcon : SidebarExpandIcon}
            aria-label={showFileTree ? 'Collapse file tree' : 'Expand file tree'}
            variant="invisible"
            size="small"
            onClick={() => setFileTreeOverride(!showFileTree)}
            className={styles.headerButton}
          />
          {!withinDialog && (
            <IconButton
              ref={closeButtonRef}
              icon={XIcon}
              aria-label="Close diff overlay"
              variant="invisible"
              size="small"
              onClick={onClose}
              className={styles.headerButton}
            />
          )}
        </div>
      </div>
      <div className={styles.body}>
        {isLoading ? (
          <div className={styles.loading}>
            <Spinner size="medium" />
          </div>
        ) : !diffs || diffs.files.length === 0 ? (
          <div className={styles.emptyState}>{changeset.error ?? 'No changes found.'}</div>
        ) : (
          <>
            <div className={styles.diffPanel} ref={diffPanelRef}>
              {changeset.error && (
                <Banner
                  title="Changed file information needs refreshing"
                  description={changeset.error}
                  variant="warning"
                />
              )}
              {diffs.files.map(file => (
                <DiffFileEntry
                  key={file.path}
                  file={file}
                  repoNwo={repoNwo}
                  headRef={headRef}
                  contentUnavailable={contentUnavailable}
                />
              ))}
            </div>
            {showFileTree && (
              <div
                className={clsx(
                  styles.fileTreePanel,
                  isMobile && fileTreeOverride === true && styles.fileTreePanelMobileOpen,
                )}
              >
                <div className={styles.fileTreePanelHeader}>
                  <span className={styles.fileTreePanelTitle}>Files</span>
                  <IconButton
                    icon={XIcon}
                    aria-label="Close file tree"
                    variant="invisible"
                    size="small"
                    onClick={() => setFileTreeOverride(false)}
                    className={styles.headerButton}
                  />
                </div>
                <PortalContext value={fileTreePortalContext}>
                  <DiffFileTree diffs={diffDeltas} fileNodeRenderer={fileNodeRenderer} sortByDiffOrder />
                </PortalContext>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

interface DiffFileEntryProps {
  file: DiffData['files'][number]
  repoNwo: string
  headRef: string
  contentUnavailable?: boolean
}

function DiffFileEntry({file, repoNwo, headRef, contentUnavailable}: DiffFileEntryProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [owner, repo] = repoNwo.split('/', 2)
  // headRef is '' for managed-sandbox sessions with no branch — guard against a broken blob link.
  const fileLinkHref =
    owner && repo && headRef ? blobPath({owner, repo, commitish: headRef, filePath: file.path}) : undefined
  const diffLines = useMemo(() => {
    if (file.lines) {
      return file.lines.map(line => ({
        type: line.type.toUpperCase() as StaticDiffLine['type'],
        html: line.html,
        text: line.text,
        left: line.left,
        right: line.right,
      }))
    }
    return file.patch ? parseUnifiedDiff(file.patch) : []
  }, [file.lines, file.patch])

  return (
    <div data-file-path={file.path} id={file.path}>
      <DiffFileHeader
        isCollapsed={!isExpanded}
        onToggleFileCollapsed={() => setIsExpanded(v => !v)}
        fileLinkHref={fileLinkHref}
        path={file.path}
        newPath={file.path}
        patchStatus={mapStatus(file.status)}
        linesAdded={file.additions}
        linesDeleted={file.deletions}
        linesChanged={file.changes}
      />
      {isExpanded && (
        <div className={styles.diffContainer}>
          {diffLines.length > 0 ? (
            <StaticUnifiedDiffPreview diffLines={diffLines} tabSize={4} className={styles.diffPreview} />
          ) : (
            <div className={styles.emptyState}>
              {contentUnavailable
                ? 'File contents are unavailable. Reconnect to the environment to load the diff.'
                : file.previewUnavailableReason === UNPREVIEWABLE_REASON.UNAVAILABLE
                  ? 'Could not load the diff for this file.'
                  : 'Binary file or no patch available.'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
